import { request } from '@/lib/api'

/**
 * Доступ к складским документам со стороны офиса (тикет #74, ТЗ §4.5).
 *
 * ## Почему отдельно от `card/api.ts`
 *
 * `card/api.ts` — универсальный слой каркаса #66: он читает и пишет **одну плоскую
 * строку** через механизм списков. Складской документ плоской строкой не описывается: у
 * приёмки есть строки с ожидаемым и принятым, у отгрузки — места и трек-номера, у
 * инвентаризации — лист пересчёта. Показать статус приёмки без её расхождений значит
 * показать заведующему складом цифру, за которой он и пришёл, без того, что она означает.
 *
 * ## Почему документы не редактируются формой
 *
 * Ни одна складская сущность не объявляет `importable` колонок в `ListCatalog`, и это
 * намеренно: остаток меняется только движением (приход, отбор, корректировка по
 * пересчёту), и каждое пишет строку в append-only `stock_movement`. Форма, ставящая
 * количество напрямую, дала бы остаток без движения, которое его объясняет, — то есть
 * расхождение, которое невозможно расследовать. Поэтому здесь только операции.
 *
 * ## Два интерфейса над одним документом
 *
 * `/api/v1/warehouse` — офис (создать, назначить, утвердить, отменить),
 * `/api/v1/wms` — терминал (пересчитать строку, подтвердить отбор, завершить).
 * Это один и тот же документ: приёмка, созданная здесь, считается на ТСД, а её
 * расхождения утверждаются снова здесь. Разделение — по роли пользователя, а не по
 * данным, и второй набор таблиц под офис означал бы два ответа на вопрос «сколько
 * приняли».
 *
 * ## Чего здесь нет — названо вслух
 *
 * - **Обёрток над пересчётом** (`/api/v1/wms/.../count`, `confirm`, `skip`). Пересчёт
 *   строки и подтверждение отбора делаются на ТСД (#52), и дублировать их в вебе значило
 *   бы позволить считать склад, не приходя на склад. Переходы документа целиком (старт,
 *   проведение, завершение) выведены в шапку карточек — см. блок в конце файла.
 * - **Печати упаковочного листа и акта.** Файлового хранилища в проекте нет, и PDF
 *   собирать нечем; данные обоих документов экран показывает, печать — браузерная.
 */

// ------------------------------------------------------------------ приёмка

/** Строка приёмки: ожидаемое рядом с принятым — расхождение видно, а не вычисляется. */
export interface ReceiptLine {
  id: string
  productId: string
  /** Название и артикул — подпись с сервера; нет поля — товара с таким id нет. */
  productName?: string | null
  sku?: string | null
  expected: number
  accepted: number
  rejected: number
  rejectionReason: string | null
}

/** Приёмка целиком. */
export interface Receipt {
  id: string
  number: string
  warehouseId: string
  supplierId: string | null
  purchaseOrderId: string | null
  status: string
  hasDiscrepancies: boolean
  lines: ReceiptLine[]
}

/**
 * Строка акта о расхождениях.
 *
 * `discrepancy` приходит **с сервера**, хотя это разность принятого и ожидаемого.
 * Считать её здесь значило бы завести второй ответ на вопрос «на сколько недопоставили»:
 * домен решает, входит ли брак в расхождение, и повторённая на клиенте формула
 * разошлась бы с ним молча.
 */
export interface Discrepancy {
  productId: string
  /** Название и артикул — подпись с сервера; нет поля — товара с таким id нет. */
  productName?: string | null
  sku?: string | null
  expected: number
  accepted: number
  rejected: number
  discrepancy: number
  reason: string | null
}

export function fetchReceipt(id: string, signal?: AbortSignal): Promise<Receipt> {
  return request<Receipt>(`/warehouse/receipts/${id}`, { signal })
}

export async function fetchDiscrepancies(
  id: string,
  signal?: AbortSignal,
): Promise<Discrepancy[]> {
  const body = await request<{ discrepancies: Discrepancy[] }>(
    `/warehouse/receipts/${id}/discrepancy-act`,
    { signal },
  )
  return body.discrepancies
}

/** Открыть приёмку. Строки — ожидаемое из заказа: расхождение надо увидеть у ворот. */
export function openReceipt(payload: {
  warehouseId: string
  supplierId: string | null
  purchaseOrderId?: string | null
  lines: { productId: string; quantity: number; unitCost?: number | null }[]
}): Promise<Receipt> {
  return request<Receipt>('/warehouse/receipts', { method: 'POST', body: payload })
}

/** Внеплановая приёмка: машина приехала без заказа, строк нет намеренно. */
export function openUnplannedReceipt(payload: {
  warehouseId: string
  supplierId: string | null
}): Promise<Receipt> {
  return request<Receipt>('/warehouse/receipts/unplanned', { method: 'POST', body: payload })
}

export function cancelReceipt(id: string): Promise<Receipt> {
  return request<Receipt>(`/warehouse/receipts/${id}/cancel`, { method: 'POST' })
}

/**
 * Передать приёмку в работу.
 *
 * Единственная терминальная операция, выведенная в веб: без неё офисный сценарий
 * «создал → посчитали → утвердил» нечем показать целиком. Право то же (`update`),
 * то есть послаблением это не является.
 */
export function startReceipt(id: string): Promise<unknown> {
  return request(`/wms/receipts/${id}/start`, { method: 'POST' })
}

// ------------------------------------------------------------------ отгрузка

/** Отгрузка: три момента раздельно — проведение, передача, доставка. */
export interface Shipment {
  id: string
  number: string
  warehouseId: string
  counterpartyId: string | null
  orderId: string | null
  status: string
  carrier: string | null
  trackingNumber: string | null
  postedAt: string | null
  handedOverAt: string | null
  deliveredAt: string | null
  packagesCount: number
  primaryDocumentId: string | null
  lines: {
    id: string
    productId: string
    productName?: string | null
    sku?: string | null
    batchId: string | null
    orderItemId: string | null
    quantity: number
  }[]
}

/**
 * Строка упаковочного листа.
 *
 * `trackingNumber` может быть пуст, и это не пробел в данных: одни перевозчики нумеруют
 * места (СДЭК, Boxberry), другие нет (ПЭК). Подставить сюда номер отгрузки значило бы
 * наклеить на коробку номер, которого у перевозчика нет, — а выглядит он рабочим.
 */
export interface PackingEntry {
  packageNo: number
  productId: string
  /** Название и артикул — подпись с сервера; нет поля — товара с таким id нет. */
  productName?: string | null
  sku?: string | null
  quantity: number
  trackingNumber: string | null
}

export function fetchShipment(id: string, signal?: AbortSignal): Promise<Shipment> {
  return request<Shipment>(`/warehouse/shipments/${id}`, { signal })
}

export async function fetchPackingList(
  id: string,
  signal?: AbortSignal,
): Promise<PackingEntry[]> {
  const body = await request<{ entries: PackingEntry[] }>(
    `/warehouse/shipments/${id}/packing-list`,
    { signal },
  )
  return body.entries
}

/**
 * Передача перевозчику.
 *
 * Отдельно от проведения: проведение — момент реализации, передача — момент, когда товар
 * физически уехал. Повторная заявка «на всякий случай» невозможна — сервер идемпотентен
 * по ключу заявки, иначе приедет второй курьер за товаром, который уже уехал.
 */
export function handOverToCarrier(id: string, carrierCode: string): Promise<Shipment> {
  return request<Shipment>(`/warehouse/shipments/${id}/hand-over`, {
    method: 'POST',
    body: { carrierCode },
  })
}

/** Отметка доставки. Момент приходит снаружи: подтверждение перевозчика запаздывает. */
export function markDelivered(id: string, deliveredAt: string | null): Promise<Shipment> {
  return request<Shipment>(`/warehouse/shipments/${id}/delivered`, {
    method: 'POST',
    body: { deliveredAt },
  })
}

// ------------------------------------------------------------------ сборка

/** Задание на сборку. `cellCount` — сколько остановок в маршруте. */
export interface PickTask {
  id: string
  number: string
  warehouseId: string
  mode: string
  status: string
  assigneeEmployeeId: string | null
  hasShortages: boolean
  cellCount: number
  lines: PickTaskLine[]
}

/**
 * Строка задания.
 *
 * `routeOrder` — порядок обхода, а не порядок отбора партий: стратегия (FIFO/FEFO)
 * выбирает, ЧТО берём, маршрут — в каком порядке идём. Смешать их значит позволить
 * порядку ходьбы молча менять, какая партия уедет клиенту.
 */
export interface PickTaskLine {
  id: string
  productId: string
  /** Название и артикул — подпись с сервера; нет поля — товара с таким id нет. */
  productName?: string | null
  sku?: string | null
  cellId: string | null
  /** Код ячейки — с сервера, и для пустой ячейки тоже; нет поля — ячейки нет. */
  cellCode?: string | null
  batchId: string | null
  quantity: number
  picked: number
  routeOrder: number
  status: string
}

export function fetchPickTask(id: string, signal?: AbortSignal): Promise<PickTask> {
  return request<PickTask>(`/warehouse/pick-tasks/${id}`, { signal })
}

export function assignPicker(id: string, employeeId: string): Promise<PickTask> {
  return request<PickTask>(`/warehouse/pick-tasks/${id}/assign`, {
    method: 'POST',
    body: { employeeId },
  })
}

/** Отмена снимает резервы: иначе отменённая сборка обещает товар несуществующему заказу. */
export function cancelPickTask(id: string): Promise<PickTask> {
  return request<PickTask>(`/warehouse/pick-tasks/${id}/cancel`, { method: 'POST' })
}

// ------------------------------------------------------------------ инвентаризация

/** Охват инвентаризации — коды `Stocktake.Scope`; склад задаётся отдельно. */
export type StocktakeScope = 'full' | 'zone' | 'cell' | 'product'

/** Инвентаризация. `scope` — охват (`full`/`zone`/`cell`/`product`), а не склад. */
export interface Stocktake {
  id: string
  number: string
  warehouseId: string
  scope: string
  zoneId: string | null
  blind: boolean
  status: string
  approvedAt: string | null
  approvedBy: string | null
  lineCount: number
}

/**
 * Строка листа пересчёта.
 *
 * `expected == null` при слепом пересчёте — именно null, а не ноль: подавление делает
 * **сервер**, и ноль читался бы как «учёт говорит, что пусто», то есть как подсказка.
 * Экран обязан показать прочерк, а не подставить нуль.
 */
export interface CountSheetLine {
  lineId: string
  productId: string
  /** Название и артикул — подпись с сервера; нет поля — товара с таким id нет. */
  productName?: string | null
  sku?: string | null
  cellId: string | null
  /** Код ячейки — с сервера, и для пустой ячейки тоже; нет поля — ячейки нет. */
  cellCode?: string | null
  batchId: string | null
  expected: number | null
  counted: number | null
}

/** Строка сличительной ведомости. `surplus` отличает излишек от недостачи. */
export interface Variance {
  productId: string
  /** Название и артикул — подпись с сервера; нет поля — товара с таким id нет. */
  productName?: string | null
  sku?: string | null
  cellId: string | null
  /** Код ячейки — с сервера, и для пустой ячейки тоже; нет поля — ячейки нет. */
  cellCode?: string | null
  batchId: string | null
  expected: number
  counted: number
  variance: number
  surplus: boolean
  comment: string | null
}

/** Проведённая корректировка: было, стало, разница. */
export interface Adjustment {
  productId: string
  /** Название и артикул — подпись с сервера; нет поля — товара с таким id нет. */
  productName?: string | null
  sku?: string | null
  cellId: string | null
  /** Код ячейки — с сервера, и для пустой ячейки тоже; нет поля — ячейки нет. */
  cellCode?: string | null
  before: number
  after: number
  delta: number
}

export function fetchStocktake(id: string, signal?: AbortSignal): Promise<Stocktake> {
  return request<Stocktake>(`/warehouse/stocktakes/${id}`, { signal })
}

export async function fetchCountSheet(
  id: string,
  signal?: AbortSignal,
): Promise<CountSheetLine[]> {
  const body = await request<{ lines: CountSheetLine[] }>(
    `/warehouse/stocktakes/${id}/count-sheet`,
    { signal },
  )
  return body.lines
}

export async function fetchVariances(id: string, signal?: AbortSignal): Promise<Variance[]> {
  const body = await request<{ variances: Variance[] }>(
    `/warehouse/stocktakes/${id}/variance-act`,
    { signal },
  )
  return body.variances
}

/**
 * Открыть инвентаризацию по складу, зоне, ячейке или товару (экран — `StocktakeCreatePage`).
 * Охват `product` (#94) — один товар по всем ячейкам склада.
 */
export function openStocktake(payload: {
  warehouseId: string
  scope: StocktakeScope
  zoneId?: string | null
  cellId?: string | null
  productId?: string | null
  blind: boolean
}): Promise<Stocktake> {
  return request<Stocktake>('/warehouse/stocktakes', { method: 'POST', body: payload })
}

/**
 * Утвердить результаты пересчёта.
 *
 * Отдельное право `approve`, и это не формальность: завершение пересчёта фиксирует, что
 * посчитали, а утверждение **меняет остаток** — деньги на балансе. Корректировка идёт
 * разницей (посчитано минус ожидалось), а не установкой: пересчёт идёт часами, и
 * отгрузка, сделанная в это время, при установке абсолютного значения молча вернулась бы
 * на полку.
 */
export async function approveStocktake(
  id: string,
  approverId?: string | null,
): Promise<Adjustment[]> {
  const body = await request<{ adjustments: Adjustment[] }>(
    `/warehouse/stocktakes/${id}/approve`,
    { method: 'POST', body: { approverId: approverId ?? null } },
  )
  return body.adjustments
}

export function cancelStocktake(id: string): Promise<Stocktake> {
  return request<Stocktake>(`/warehouse/stocktakes/${id}/cancel`, { method: 'POST' })
}

// ------------------------------------------------------------------ остаток

/** Строка остатка. `available` = количество минус зарезервированное — с сервера. */
export interface StockRow {
  id: string
  warehouseId: string
  productId: string
  /** Название и артикул — подпись с сервера; нет поля — товара с таким id нет. */
  productName?: string | null
  sku?: string | null
  cellId: string | null
  /** Код ячейки — с сервера, и для пустой ячейки тоже; нет поля — ячейки нет. */
  cellCode?: string | null
  batchId: string | null
  quantity: number
  reserved: number
  available: number
  unitCost: number | null
  ownerCounterpartyId: string | null
}

/** Резерв на строке остатка: отвечает на «почему нельзя отгрузить, он же лежит». */
export interface Reservation {
  id: string
  stockId: string
  productId: string
  warehouseId: string
  quantity: number
  orderId: string | null
  status: string
  expiresAt: string | null
}

export async function fetchStockOf(
  productId: string,
  warehouseId: string,
  signal?: AbortSignal,
): Promise<StockRow[]> {
  const body = await request<{ rows: StockRow[] }>(
    `/warehouse/stock?productId=${productId}&warehouseId=${warehouseId}`,
    { signal },
  )
  return body.rows
}

export async function fetchReservations(
  stockId: string,
  signal?: AbortSignal,
): Promise<Reservation[]> {
  const body = await request<{ reservations: Reservation[] }>(
    `/warehouse/stock/${stockId}/reservations`,
    { signal },
  )
  return body.reservations
}

// ------------------------------------------------------------------ перемещения

/** Перемещение между складами. */
export interface Transfer {
  id: string
  number: string
  fromWarehouseId: string
  toWarehouseId: string
  status: string
  dispatchedAt: string | null
  receivedAt: string | null
  hasTransitLoss: boolean
  lines: TransferLine[]
}

/**
 * Строка перемещения.
 *
 * `counted` — отдельный флаг, а не вывод из `received > 0`: колонка в схеме
 * `NOT NULL DEFAULT 0`, и ноль в ней одновременно значит «ничего не доехало» и «строку не
 * смотрели» (V15). Первое — претензия к перевозчику, второе — незаконченная работа, и
 * показать их одинаково значит потерять разницу.
 */
export interface TransferLine {
  id: string
  productId: string
  /** Название и артикул — подпись с сервера; нет поля — товара с таким id нет. */
  productName?: string | null
  sku?: string | null
  batchId: string | null
  fromCellId: string | null
  fromCellCode?: string | null
  toCellId: string | null
  toCellCode?: string | null
  quantity: number
  received: number
  counted: boolean
  shortfall: number
}

export function fetchTransfer(id: string, signal?: AbortSignal): Promise<Transfer> {
  return request<Transfer>(`/warehouse/transfers/${id}`, { signal })
}

export function createTransfer(payload: {
  fromWarehouseId: string
  toWarehouseId: string
  lines: { productId: string; batchId?: string | null; fromCellId?: string | null; quantity: number }[]
}): Promise<Transfer> {
  return request<Transfer>('/warehouse/transfers', { method: 'POST', body: payload })
}

/** Отправка списывает товар с остатка отправителя: он уже в машине, а не на полке. */
export function dispatchTransfer(id: string): Promise<Transfer> {
  return request<Transfer>(`/warehouse/transfers/${id}/dispatch`, { method: 'POST' })
}

export function cancelTransfer(id: string): Promise<Transfer> {
  return request<Transfer>(`/warehouse/transfers/${id}/cancel`, { method: 'POST' })
}

// ------------------------------------------------------------------ проверки

/**
 * Сходится ли принятое с ожидаемым по строке приёмки.
 *
 * Существует ради **проверки**, а не отображения: числа приходят с сервера, и повторив
 * формулу ради показа, мы завели бы второй ответ на вопрос «недопоставили ли».
 * Здесь она отвечает на другой вопрос — «надо ли подсветить строку».
 */
export function receiptLineMatches(line: ReceiptLine): boolean {
  return line.accepted + line.rejected === line.expected
}

/**
 * Потеря в пути по строке перемещения.
 *
 * Считается **только** по строкам, которые смотрели: непосчитанная строка — это
 * незаконченная работа, и объявить её потерей значит выставить претензию перевозчику за
 * то, чего никто не проверял.
 */
export function transitLoss(line: TransferLine): number {
  return line.counted ? line.shortfall : 0
}

// ------------------------------------------------------------------ раскладки на дизайн-системе

/*
 * Ниже — то, что понадобилось раскладкам складских документов по макетам (Receiving,
 * Shipment, Picking, Inventory, Transfer.dc.html): действия, которые макет ставит в шапку,
 * и имена товаров и коды ячеек вместо обрезанных идентификаторов.
 *
 * Действия идут через `/api/v1/wms` — это **те же** операции, что вызывает терминал, под
 * тем же правом `update`, а не офисные дубли: офисных эндпоинтов у проведения приёмки и
 * отгрузки, завершения сборки, пересчёта и приёмки перемещения нет, и второй путь к одному
 * переходу состояния разошёлся бы с первым. Пересчёт строк по-прежнему только на терминале:
 * в вебе нет ни одного вызова, который ставит количество.
 *
 * Ключ идемпотентности рождается **при открытии подтверждения** и повторяется при повторе
 * после сбоя: проведение, отправленное дважды из-за потерянного ответа, списало бы остаток
 * дважды, а фильтр сервера на тот же ключ возвращает сохранённый ответ.
 */

/** Справочник товаров документа с терминального эндпоинта: имена для экрана. */
export interface Catalogue {
  names: Record<string, string>
}

/**
 * Приёмка так, как её видит терминал.
 *
 * Нужна ради двух вещей, которых нет в офисной карточке: имён товаров и признака
 * «строку пересчитали». Признак сервер выводит из количеств (колонки в схеме нет), и для
 * честного нуля он неверен — это названо в `TerminalOperationsService.ReceiptLineView`.
 */
export interface ReceiptTerminalView {
  id: string
  status: string
  lines: { id: string; productId: string; counted: boolean }[]
  catalogue: Catalogue
}

export function fetchReceiptTerminal(id: string, signal?: AbortSignal): Promise<ReceiptTerminalView> {
  return request<ReceiptTerminalView>(`/wms/receipts/${id}`, { signal })
}

/** Подсказка размещения после проведения: что и в какую ячейку класть. */
export interface Putaway {
  itemId: string
  productId: string
  quantity: number
  cellId: string | null
  /** Код ячейки — с сервера, и для пустой ячейки тоже; нет поля — ячейки нет. */
  cellCode?: string | null
  crossDocked: boolean
}

/** Провести приёмку: товар встаёт на остаток, сервер отвечает подсказками размещения. */
export async function completeReceipt(id: string, idempotencyKey: string): Promise<Putaway[]> {
  const body = await request<{ putaway: Putaway[] }>(`/wms/receipts/${id}/complete`, {
    method: 'POST',
    idempotencyKey,
  })
  return body.putaway
}

/**
 * Провести отгрузку: списать остаток и выпустить первичный документ.
 *
 * Вид документа не передаётся — сервер берёт УПД, и выбор вида здесь стал бы вторым
 * местом, где это решение принимается.
 */
export function postShipment(id: string, idempotencyKey: string): Promise<unknown> {
  return request(`/wms/shipments/${id}/post`, { method: 'POST', body: {}, idempotencyKey })
}

/** Остановка маршрута сборки: код ячейки и порядок обхода — с сервера. */
export interface RouteStop {
  order: number
  lineId: string
  productId: string
  cellCode: string | null
  quantity: number
  status: string
  pending: boolean
}

/** Маршрут сборки. `nextStopIndex` считает сервер: правило «первая незавершённая» одно. */
export interface PickRoute {
  taskId: string
  status: string
  nextStopIndex: number | null
  stops: RouteStop[]
  catalogue: Catalogue
}

export function fetchPickRoute(id: string, signal?: AbortSignal): Promise<PickRoute> {
  return request<PickRoute>(`/wms/pick-tasks/${id}/route`, { signal })
}

/** Недобор по строке задания, названный завершением сборки. */
export interface PickShortage {
  productId: string
  required: number
  picked: number
  shortfall: number
}

/** Завершить сборку. Сервер откажет, пока есть необработанные строки. */
export async function completePickTask(id: string, idempotencyKey: string): Promise<PickShortage[]> {
  const body = await request<{ shortages: PickShortage[] }>(`/wms/pick-tasks/${id}/complete`, {
    method: 'POST',
    idempotencyKey,
  })
  return body.shortages
}

/**
 * Инвентаризация с терминала — только ради имён товаров листа.
 *
 * Ожидаемое количество из этого ответа экран не читает вовсе: лист берётся офисным
 * эндпоинтом, и подавление при слепом пересчёте делает сервер в обоих.
 */
export interface StocktakeTerminalView {
  id: string
  catalogue: Catalogue
}

export function fetchStocktakeTerminal(id: string, signal?: AbortSignal): Promise<StocktakeTerminalView> {
  return request<StocktakeTerminalView>(`/wms/stocktakes/${id}`, { signal })
}

/** Начать пересчёт: лист уходит на терминалы. */
export function startStocktake(id: string, idempotencyKey: string): Promise<unknown> {
  return request(`/wms/stocktakes/${id}/start`, { method: 'POST', idempotencyKey })
}

/**
 * Завершить пересчёт.
 *
 * Остаток **не корректирует**: это фиксация того, что посчитали. Утверждение — отдельное
 * действие и другое право (`approve`).
 */
export function finishCounting(id: string, idempotencyKey: string): Promise<unknown> {
  return request(`/wms/stocktakes/${id}/finish-counting`, { method: 'POST', idempotencyKey })
}

/** Перемещение с терминала — ради имён товаров. */
export interface TransferTerminalView {
  id: string
  catalogue: Catalogue
}

export function fetchTransferTerminal(id: string, signal?: AbortSignal): Promise<TransferTerminalView> {
  return request<TransferTerminalView>(`/wms/transfers/${id}`, { signal })
}

/** Потеря в пути, зафиксированная завершением приёмки перемещения. */
export interface TransitLossLine {
  productId: string
  sent: number
  received: number
  shortfall: number
}

/**
 * Завершить приёмку перемещения.
 *
 * Сервер отказывает, пока хоть одна строка не пересчитана: строка без ответа оставила бы
 * товар внутри закрытого перемещения — там, где недостачу никто не ищет.
 */
export async function completeTransferReceipt(id: string, idempotencyKey: string): Promise<TransitLossLine[]> {
  const body = await request<{ losses: TransitLossLine[] }>(`/wms/transfers/${id}/complete`, {
    method: 'POST',
    idempotencyKey,
  })
  return body.losses
}

